import {
  PAYROLL_VOUCHER_STATUS_LABELS,
} from "@/enum/payroll-voucher-status";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatToBRL } from "@/utils/formatToMoney";
import { Inbox, Link2 } from "lucide-react";
import { COLUMN_TITLES, STATUS_BADGE_CLASS_NAME } from "../constants";
import { redactCpf } from "@/utils/cpf-mask";
import { AssignmentsEnum } from "@/enum/assignments";
import { useAssignmentAbilities } from "@/hooks/use-assignment-permission";
import { PAYROLL_VOUCHER_LABELS } from "../messages";
import { PayrollVoucher, VoucherDecision } from "../types";
import { installmentsOf } from "../utils/decision-consequence";
import { VoucherRowActions } from "./voucher-row-actions";

interface VoucherCardsProps {
  vouchers: PayrollVoucher[];
  onDecide: (
    voucher: PayrollVoucher,
    decision: VoucherDecision,
  ) => Promise<void>;
}

interface CardFieldProps {
  label: string;
  value: string;
}

function CardField({ label, value }: CardFieldProps): JSX.Element {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800">{value}</span>
    </div>
  );
}

export function VoucherCards({
  vouchers,
  onDecide,
}: VoucherCardsProps): JSX.Element {
  const { canUpdate } = useAssignmentAbilities(AssignmentsEnum.PAYROLL_VOUCHER);

  if (vouchers.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white p-6 text-muted-foreground md:hidden">
        <Inbox className="h-6 w-6" />
        {PAYROLL_VOUCHER_LABELS.EMPTY_STATE}
      </div>
    );
  }

  return (
    <div className="space-y-3 md:hidden">
      {vouchers.map((voucher: PayrollVoucher) => (
        <div
          key={voucher.id}
          className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium text-gray-900">
                {voucher.employee_name}
              </p>
              <span
                className="flex items-center gap-1 text-xs text-gray-500"
                title={PAYROLL_VOUCHER_LABELS.CONCILIATION_LINK_TITLE}
              >
                <Link2 className="h-3 w-3" />
                {PAYROLL_VOUCHER_LABELS.CONCILIATION_LINK(
                  voucher.conciliation_id,
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span
                className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS_NAME[voucher.status]}`}
                title={voucher.rejection_reason ?? undefined}
              >
                {PAYROLL_VOUCHER_STATUS_LABELS[voucher.status]}
              </span>
              <VoucherRowActions
                voucher={voucher}
                canDecide={canUpdate}
                onDecide={onDecide}
              />
            </div>
          </div>

          <div className="space-y-1">
            <CardField
              label={COLUMN_TITLES.CPF}
              value={redactCpf(voucher.employee_cpf)}
            />
            <CardField
              label={COLUMN_TITLES.BRANCH}
              value={voucher.store_code}
            />
            <CardField
              label={COLUMN_TITLES.AMOUNT}
              value={formatToBRL(voucher.amount)}
            />
            <CardField
              label={COLUMN_TITLES.INSTALLMENTS}
              value={`${installmentsOf(voucher)}x`}
            />
            <CardField
              label={COLUMN_TITLES.DATE}
              value={formatDateToDMY(voucher.conciliation_date)}
            />
          </div>

          {voucher.analyst_justification && (
            <p className="w-fit rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {voucher.analyst_justification}
            </p>
          )}

          {voucher.decided_by_email && (
            <p className="text-xs text-gray-500">
              {PAYROLL_VOUCHER_LABELS.DECIDED_BY(voucher.decided_by_email)}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
